import { ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild, AfterViewChecked } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { FilterTextPipe } from '../pipes/filter-text.pipe';
import { SelectorFilterMetric, MetricSelectorData, MetricType, SelectedMetric, SelectorMetric } from './metric-selector.model';
import { MetricSelectorService } from './metric-selector.service';
import { CVLocalizationService } from '../../reusables/services/cv-localization.service';
import { CvConfigurationService } from '../services/cv-configuration.service';
import { DashboardService } from 'src/app/dashboard/dashboard.service';
import { MetricTypes } from '../../../shared/models/pipeline-config/mongo-enums.model';
import { TimeType } from '../../../shared/models/metric';
import { NumericType } from 'src/shared/models/metric';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Modules } from 'src/shared/models/module-info.model';

@Component({
    selector: 'cv-metric-selector',
    templateUrl: './metric-selector.component.html',
    styleUrls: ['./metric-selector.component.scss']
})
export class MetricSelectorComponent implements OnInit {
    
    @ViewChild('favTooltipRef') set favTooltipRef(content: ElementRef) {
        if (content) {
            this.favTooltip = content.nativeElement;
        }
    }
    data: MetricSelectorData = null;
    groupHeadings = null;
    metricsReady = false;
    filterText = '';
    editFilterText = '';
    filteredCategories: string[] = null;
    selectedMetrics: SelectedMetric[] = [];
    isSaving = false;
    favMetrics: SelectorMetric[] = [];
    allFavs: SelectorMetric[] = [];
    isFavExpanded = false;
    renamePermission = false;
    isEditing = false;
    editingMetric: any = null;
    metricCustomName = "";
    maxMetricTooltipText = "";
    tooltipTimeout; //max metric tooltip
    popoverTimeout; //metric def popover
    showTooltip = false;
    mouseX = 0;
    mouseY = 0;
    favTooltipShowing = false;
    favTooltip: HTMLElement = null;
    metricTooltip: Element = null;
    currentMetric: any;
    currentPopover: any;
    showDetails = false;
    formulaLoading = false;
    previousDropdown: HTMLElement = null;
    filterMetric: SelectorFilterMetric;
    filterMetrics: SelectorFilterMetric[] = [];
    metricToFilter;
    minValue;
    maxValue;
    searchPlaceholder = "search";
    renamedMetrics = [];
    calculatedMetric: SelectedMetric = null;
    relatedMetrics: SelectedMetric[] = null;
    relatedMetricsTooltipValue: string;

    get relatedMetricsDisplay(): SelectedMetric[]{
        let i =0;
        const returnMetrics = [];
        for(let j=0; j < this.relatedMetrics.length; j++)
        {
            if(this.displayRelatedMetric(this.relatedMetrics[j]) && i<3){
                returnMetrics.push(this.relatedMetrics[j]);
                i++;
            }
            else if (i >= 3)
                break;
        }
        return returnMetrics;
    }
    numericTypes: any[] = [];
    filteredNumericTypes: any[] = [];
    localizedFilter: string = "Filter";
    localizedDropdownLabel: string = "Format Type";

    constructor(
        public activeModal: NgbActiveModal,
        public service: MetricSelectorService,
        public cvLocalizationService: CVLocalizationService,
        public config: CvConfigurationService,
        public cd: ChangeDetectorRef,
        public dashboardService: DashboardService,
        private ngbModal: NgbModal,
        public changeDetector: ChangeDetectorRef,
    ) {
        $(document).on('click', (event) => {
            if (!$(event.target).closest('.msActnItm').length) {
                this.previousDropdown?.classList.remove('d-block');
            }
        });
    }

    ngOnInit() {
        this.ngOnInitAsync();
    }

    async ngOnInitAsync() {
        await this.loadLocalizations();
        try {
            const isReport = this.dashboardService.isReportDashboard(); 
            this.data = await this.service.getMetricSelectorData(isReport);
            this.renamePermission = await this.service.getRenamePermission();
            await this.cvLocalizationService.GetSection("Metrics");
        }
        catch {
            this.metricsReady = false;
        }

        this.metricsReady = true;
        this.filteredCategories = this.data.MetricCategories;
        this.isFavExpanded = this.data.IsFavExpanded;
        this.fillFavorites();
        this.fillSelectedMetrics();
        await this.fillNumericTypeDropdown();
        this.editFilterText = this.cvLocalizationService.getCachedText("Metric_Selector", "Edit_Filter", "Edit Filter");

        if (this.data.HasFilters) {
            //only wallboards uses filters and it handles max metrics differently
            this.fillFilterMetrics();
            this.service.maxMetrics = this.data.SlideMetricMax;
        }

        if(this.data.LastMetricSelected)  // the last selected metric is saved in the state
            this.fillCalculatedMetrics(this.data.SelectorMetrics.find(m => m.MetricID === this.data.LastMetricSelected));
        else if(this.selectedMetrics?.length === 1 && this.selectedMetrics[0].Formula && this.selectedMetrics[0].Formula !== "") // without a state, if there is only one metric, that is the previous selected metric
            this.fillCalculatedMetrics(this.selectedMetrics[0])

        //get tooltip text after checking for filters since wallboards handles max metrics differently
        this.maxMetricTooltipText = await this.getMaxMetricText();
        this.relatedMetricsTooltipValue = await this.loadRelatedMetricsTooltip();
    }

    async loadRelatedMetricsTooltip()
    {
        return await this.cvLocalizationService.getCachedText("Metric_Selector", "Related_Metrics_Tooltip", "These metrics are specifically chosen to match your previous selections.")
    }

    onCancelClicked() {
        if(this.renamedMetrics.length > 0){
            const renamed = {renamedMetrics: this.renamedMetrics, renameOnly: true};
            this.activeModal.close(renamed);
        }
        else{
        this.activeModal.close(false);
        }
        this.calculatedMetric = null;
        this.relatedMetrics = [];
        this.renamedMetrics= [];
        this.service.filterParams = null;
    }

    async loadLocalizations() {
        await this.cvLocalizationService.GetSection("Metric_Selector");
        await this.cvLocalizationService.GetSection("Pipeline_Configuration");
        await this.cvLocalizationService.GetSection("Metrics");
        await this.cvLocalizationService.GetSection("Date_Paradigm_Filter");
        this.searchPlaceholder = this.cvLocalizationService.getCachedText("Metric_Selector", "Search_In_Metrics", "Search in Metrics");
    }

    async onConfirmClicked() {
        if (this.isSaving) return;

        this.isSaving = true;

        if (!this.service.singleMetric) {
            await this.service.updateSelectedMetrics(this.selectedMetrics, this.calculatedMetric?.MetricID);

            if (this.data.HasFilters)
                await this.service.updateFilterMetrics(this.data.MetricFilters);
        }
        this.activeModal.close(this.selectedMetrics);
        this.isSaving = false;
        this.renamedMetrics= [];
        this.calculatedMetric = null;
        this.relatedMetrics = [];
        this.service.filterParams = null;
    }

    getMetricsByCategory(category) {
        if(this.filteredNumericTypes.length == this.numericTypes.length) // If All is selected, User Attributes should be included
            return this.data.SelectorMetrics.filter(m => m.MetricGroup === category);

        return this.data.SelectorMetrics.filter(m => (this.filteredNumericTypes.includes(m.NumericType) && m.MetricGroup === category));
    }

    shouldGrayOut(metric: SelectedMetric) {
        let blockMetric = true;

        //If the selector does not have the "view by" setting
        if (!this.data.EmpShowToggle && !this.data.RecShowToggle || (metric.MetricID < 0 && this.service.includeAttMetrics)) //negative metricIDs will be user attributes
            blockMetric = false;

        if (this.data?.EmpSelectedToggle) {
            if (metric.EmpLevelMin <= this.data?.EmpSelector.SelectedGroupLevel) {
                blockMetric = false;
            }
        }
        else if (this.data?.RecSelectedToggle) {
            if (metric.RecLevelMin <= this.data?.RecSelector.SelectedGroupLevel) {
                blockMetric = false;
            }
        }
        else
            blockMetric = false;
        // If the widget only requires numeric metrics, metric numeric type should be checked
        // disabling percent metrics for metric breakdown
        if (this.service.disabledNumericMetrics &&
            this.service.disabledNumericMetrics.length > 0 &&
            this.service.disabledNumericMetrics.includes(metric.NumericType)
        ) {
            blockMetric = true;
        }
        if (this.service.moduleId === Modules.CXOneMetricBreakdown && metric.CanBeNegative) {
            blockMetric = true;
        }

        return blockMetric;
    }

    filterCategories() {
        const filteredMetrics: SelectorMetric[] = new FilterTextPipe()
            .transform(this.data.SelectorMetrics, this.filterText, 'Abbreviation');
        this.filterFavSection(filteredMetrics);
        this.filterBodySection(filteredMetrics);
    }

    filterBodySection(filteredMetrics: SelectorMetric[]) {
        const categories = new Array<string>();
        filteredMetrics.forEach( m => {
            if (!categories?.includes(m.MetricGroup) &&
                (this.filteredNumericTypes.includes(m.NumericType) || this.filteredNumericTypes.length == this.numericTypes.length)) {
                categories?.push(m.MetricGroup);
            }
        });
        if (categories) {
            this.filteredCategories = categories;
        }
    }

    filterFavSection(filteredMetrics: SelectorMetric[]) {
        this.favMetrics = this.allFavs;
        this.favMetrics = this.favMetrics.filter(fav => filteredMetrics
            .find(filteredMetric => fav.MetricID === filteredMetric.MetricID));
    }

    scroll(category) {
        const el = document.getElementById(category + '-header');
        let topOfTarget = el.offsetTop;
        if (!this.isFavExpanded) {
            topOfTarget -= 145;
            $("#innerMetricSelectorModule", "body").animate({ scrollTop: topOfTarget }, 1000, "easeOutExpo");
        }
        else {
            topOfTarget -= 320;
            $("#innerMetricSelectorModule", "body").animate({ scrollTop: topOfTarget }, 1000, "easeOutExpo");
        }
        $("#innerMetricSelectorModule", "body").animate({ scrollTop: topOfTarget }, 1000, "easeOutExpo");
    }

    isSelected(metric: SelectedMetric) {
        return this.selectedMetrics.some(m => m.MetricID === metric.MetricID);
    }

    isFavMetric(metric: SelectorFilterMetric | SelectedMetric) {
        return this.allFavs.find(m => m.MetricID === metric.MetricID);
    }

    toggleSelection(metric: SelectedMetric, skipCalculatedMetrics: boolean = false) {
        const isSelected = this.isSelected(metric);
        // Try to remove first
        if (isSelected) {
            const index = this.selectedMetrics.findIndex(m => m.MetricID === metric.MetricID);
            if (index > -1) this.selectedMetrics.splice(index, 1);
            if (!skipCalculatedMetrics || metric === this.calculatedMetric) {
                this.calculatedMetric = null;
                this.relatedMetrics = [];
            }
            return;
        }
        // If not selected, try to add
        if (this.shouldGrayOut(metric) ||
            (this.selectedMetrics.length >= this.service.maxMetrics) ||
            this.isEditing) {
            return;
        }
        metric.TimeTypeOverride ??= metric.TimeType;
        // Loads Metric DateFields 
        this.loadMetricDateFields(metric);

        // Sets default Date Paradigm 
        // this.setDefaultDateParadigm(metric); // remove for 25.3

        this.selectedMetrics.push(metric);
        
        if (skipCalculatedMetrics) return;
        
        if (metric.Formula && metric.Formula !== "") {
            this.fillCalculatedMetrics(metric);
        } else {
            this.calculatedMetric = null;
            this.relatedMetrics = [];
        }
    }

    async toggleFavorite(event: MouseEvent, metric) {
        event.stopPropagation();
        if (!this.isFavMetric(metric)) {
            this.favMetrics.push(metric);
            this.allFavs.push(metric);
        }
        else {
            const item = this.favMetrics.findIndex(m => m.MetricID === metric.MetricID);
            if (item > -1) {
                this.favMetrics.splice(item, 1);
                this.allFavs.splice(item, 1);
                this.leave();
            }
        }
        await this.service.updateFavorites(metric.MetricID);
    }


    fillSelectedMetrics() {
        this.service.selectedMetrics?.forEach(metric => {
            let metricMatch = this.data.SelectorMetrics?.find(m => m.MetricID === metric.Id);
            if (!this.service.singleMetric) //Single Metric Selector doesn't use timetype overrides
                metricMatch.TimeTypeOverride ??= this.data.TimeTypeOverrides ? this.data?.TimeTypeOverrides[metric.Id] : TimeType.None;
            if(metricMatch)
                // Loads the DateFields of each of the selected metrics
                this.loadMetricDateFields(metricMatch);

                // Assigns previously selected DateParadigm to metric.
                //metricMatch.DateParadigm = this.data.DateParadigmNames ? this.data?.DateParadigmNames[metric.Id] : null; // remove for 25.3
                this.selectedMetrics.push(metricMatch);             
        });
    }

    fillFavorites() {
        this.data.FavoriteIdsOrder.forEach(id => {
            const metricMatch = this.data.SelectorMetrics?.find(m => m.MetricID === id);
            if (metricMatch) {
                this.favMetrics.push(metricMatch);
                this.allFavs.push(metricMatch);
            }
        });
    }

    fillFilterMetrics() {
        this.data.MetricFilters.forEach(metric => {
            const metricMatch = this.data.SelectorMetrics?.find(m => m.MetricID === metric.MetricID);
            if (metricMatch) {
                this.filterMetrics.push({
                    Abbreviation: metricMatch.Abbreviation,
                    MetricCustomName: metricMatch.MetricCustomName,
                    MetricID: metric.MetricID,
                    MaxValue: metric.MaxValue,
                    MinValue: metric.MinValue,
                    NumericType: metricMatch.NumericType,
                } as SelectorFilterMetric);
            }
        })
    }

    async fillNumericTypeDropdown()
    {
        const values = Object.values(NumericType).filter(v => !Number.isFinite(v));
        for(var i =0 ; i < values.length; i++)
        {
            const localizedNumericType = this.cvLocalizationService.getCachedText("Metrics", values[i].toString(), values[i].toString())
            this.numericTypes.push({index: i, value: localizedNumericType})
            this.filteredNumericTypes.push(i)
        }

        this.localizedFilter = await this.cvLocalizationService.GetLocalText("Metric_Messages", "filter", "Filter");
        this.localizedDropdownLabel = await this.cvLocalizationService.GetLocalText("Pipeline_Configuration", "Format_Type", "Format Type");
    }
    
    onNumericTypeSelectionChanged(event) {
        if(this.filteredNumericTypes.length === event.value.length)
        {
            this.filteredNumericTypes = [];
            return;
        }
        this.filteredNumericTypes = event.value;
    }

    fillCalculatedMetrics(metric)
    {
        this.relatedMetrics = [];
        this.calculatedMetric = metric;
        const regex = /#(\d+)#/g;
        const metricIDs = new Set();
        let id;

        while((id =regex.exec(metric.Formula)) !== null)
        {
            metricIDs.add(parseInt(id[1]))
        }
        
        metricIDs.forEach(id => 
        {
            const matchedMetric =this.data.SelectorMetrics?.find(m => m && m.MetricID === id);
            if(matchedMetric)
                this.relatedMetrics.push(matchedMetric)
        })
    }

    displayRelatedMetric(metric)
    {
        return this.selectedMetrics.indexOf(metric) < 0;
    }

    displayAnyRelatedMetrics()
    {
        for(var i = 0; i < this.relatedMetrics.length ; i++)
        {
            if(this.displayRelatedMetric(this.relatedMetrics[i]))
                return true;
        }
        return false;
    }

    clearAll() {
        this.selectedMetrics = [];
        this.calculatedMetric = null;
        this.relatedMetrics = [];
    }

    async toggleFavDropdown() {
        $('.favoriteSection')[$('.favoriteSection').length - 1].scrollTop = 0;
        this.isFavExpanded = !this.isFavExpanded;
        //save state of dropdown
        await this.service.updateFavDropdown(this.isFavExpanded);
    }

    toggleEdit(event: MouseEvent, metric){
        event?.stopPropagation();
        this.metricCustomName = this.getMetricDisplayName(metric);
        this.isEditing = !this.isEditing;
        if (this.isEditing)
            this.leave();
        this.editingMetric = this.isEditing ? metric : null;
    }

    isEditingMetric(metric){
        if(this.editingMetric === null)
            return false;

        return metric.MetricID === this.editingMetric.MetricID;
    }

    checkChanges(event, metric) {
        const validLength = this.metricCustomName.length > 0 && this.metricCustomName.length <= 50;
        if(this.metricCustomName === metric.MetricCustomName && metric.Abbreviation !== this.metricCustomName)
        {
            this.toggleEdit(event, metric);
        }
        else if(validLength //Update the custom name or
        || this.metricCustomName === "") // Remove the custom name
        {
            this.applyCustomName(this.editingMetric);
            this.toggleEdit(event, metric);
        }
    }

    async applyCustomName(metric) {
        const applyString = metric.Abbreviation === this.metricCustomName ? "" : this.metricCustomName;

        metric.MetricCustomName = applyString;

        const selectedMetric = this.selectedMetrics.filter(m => m.MetricID === metric.MetricID);
        selectedMetric.forEach(m => m.MetricCustomName = applyString);

        const favoriteMetric = this.favMetrics.filter(m => m.MetricID === metric.MetricID);
        favoriteMetric.forEach(m => m.MetricCustomName = applyString);

        const filterMetrics = this.filterMetrics.filter(m => m.MetricID === metric.MetricID);
        filterMetrics.forEach(m => m.MetricCustomName = applyString);

        const dashboardKey = this.service.canRenameMetrics && this.dashboardService.activeDashboard ? this.dashboardService.activeDashboard.key : null;

        await this.service.updateCustomMetricNames(metric, dashboardKey, this.metricCustomName);
        this.renamedMetrics.push(metric);
    }

    public getIsMetricRenamed(metric)
    {
        return metric.MetricCustomName && metric.MetricCustomName !== "";
    }

    public getMetricDisplayName(metric)
    {
        if (this.getIsMetricRenamed(metric) && this.service.canRenameMetrics)
            return metric.MetricCustomName;

        return metric.Abbreviation;
    }

    public getMetricDisplayNameTruncated(metric){
    const displayName = this.getIsMetricRenamed(metric) && this.service.canRenameMetrics ? metric.MetricCustomName + "*" : metric.Abbreviation;
    if(this.relatedMetricsDisplay.length==1)
        return displayName;
    else if(this.relatedMetricsDisplay.length==2){
        const length = displayName.length > 24 ? 24 : displayName.length;
        const returnedName = displayName.length == length ? displayName : displayName.substring(0, length) + "...";
        return returnedName;
    }
    const length = displayName.length > 11 ? 11 : displayName.length;
    const returnedName = displayName.length == length ? displayName : displayName.substring(0, length) + "...";
    return returnedName;
    }

    getMetricTypeName(metric) {
        let metricType;
        switch (metric.MetricType) {
            case MetricType.Derived:
                metricType = this.cvLocalizationService
                    .getCachedText("Pipeline_Configuration", "Metric_Component_Type_Calculated", "Calculated Metric");
                break;
            case MetricType.Snapshot:
                metricType = this.cvLocalizationService
                    .getCachedText("Pipeline_Configuration", "Metric_Component_Type_Snapshot", "Snapshot Metric");
                break;
            case MetricType.CurrentStatesMetricData:
                metricType = this.cvLocalizationService
                    .getCachedText("Pipeline_Configuration", "Metric_Component_Type_Real_Time", "Real-Time Metric");
                break;
            case MetricType.Attribute:
                metricType = "";
                break;
            default:
                metricType = this.cvLocalizationService
                    .getCachedText("Pipeline_Configuration", "Metric_Component_Type_Standard", "Standard Metric");
                break;
        }
        return metricType;
    }

    isStandard(metric) {
        let value;
        switch (metric.MetricType) {
            case MetricType.Derived:
            case MetricType.Snapshot:
            case MetricType.CurrentStatesMetricData:
            case MetricType.Attribute:
                value = false;
                break;
            default:
                value = true;
                break;
        }
        return value;
    }

    getMetricTypeDescription(metric) {
        let metricDesc;
        switch (metric.MetricType) {
            case MetricType.Derived:
                metricDesc = this.cvLocalizationService
                    .getCachedText("Metrics", "Calculated_Desc", "A value created from mathematical operations using other metrics.");
                break;
            case MetricType.Snapshot:
                metricDesc = this.cvLocalizationService.getCachedText("Metrics", "Snapshot_Desc",
                    `A value derived from one or more components. In which, the value for each is set as a count, duration,
                     or calculated duration between two time fields. 
                     This metric is not bound to a date, and will display the same value regardless
                     of the date range selected in a widget.`);
                break;
            case MetricType.CurrentStatesMetricData:
                metricDesc = this.cvLocalizationService.getCachedText("Metrics", "RealTime_Desc",
                    `A value directly pulled straight from an API. 
                    This is set as what ever the current value is in the related system as quickly as it is fed to us.`);
                break;
            case MetricType.Attribute:
                metricDesc = "";
                break;
            default:
                metricDesc = this.cvLocalizationService.getCachedText("Metrics", "Standard_Desc",
                    `A value derived from one or more components. In which, the value for each is set as a count,
                     duration, or calculated duration between two time fields.
                     This metric must been bound to a date.`);
                break;
        }
        return metricDesc;
    }

    async getMaxMetricText() {
        const text = await this.cvLocalizationService
            .GetLocalText("Metric_Selector", "Max_Metrics_Tooltip", `Up to ${this.service.maxMetrics} metrics can be selected`);
        return await this.cvLocalizationService.Format(text, this.service.maxMetrics);
    }

    followCursor(event, id) {
        this.mouseY = event.clientY;
        this.mouseX = event.pageX;

        if (this.favTooltipShowing) {
            const maxTooltip = document.getElementById(`msTooltipMax`);
            maxTooltip.classList.remove("d-block");
            return;
        }

        if (this.showTooltip) {
            let el = this.getTooltip(event.currentTarget, id);
            if (!el || this.calculateTop(el) === 0)
                return;
            el.classList.add("d-block");
            this.positionTooltip(el);
        }
    }

    enter(event, id) {
        if(this.isEditing)
            return;
        if (this.tooltipTimeout) {
            clearTimeout(this.tooltipTimeout);
            this.tooltipTimeout = null;
        }

        this.tooltipTimeout = setTimeout(()=> {
            this.showTooltip = true;
            this.metricTooltip = this.getTooltip(event.srcElement, id);
            if (!this.metricTooltip || this.calculateTop(this.metricTooltip) === 0)
                return;
            this.metricTooltip.classList.add("d-block");
            this.positionTooltip(this.metricTooltip);
            if (this.favTooltip?.classList.contains('hover-tooltip')) {
                this.metricTooltip.classList.remove("d-block");
            }
        }, 1000);
    }

    leave() {
        let maxTooltip = document.getElementById("msTooltipMax");
        maxTooltip.classList.remove("d-block");
        this.metricTooltip = null;

        if (this.tooltipTimeout) {
            clearTimeout(this.tooltipTimeout);
            this.tooltipTimeout = null;
        }

        this.showTooltip = false;
    }

    public enterPressed(event, metric) {
        if (event.keyCode === 13) {
            this.checkChanges(event, metric);
        }
    }

    getTooltip(metric, id) {
        let tooltip;
        if (metric.classList.contains("maxSelected")) {
            tooltip = document.getElementById("msTooltipMax");
        }
        return tooltip;
    }

    positionTooltip(tooltip) {
        const top = this.calculateTop(tooltip);

        tooltip.style.left = (this.mouseX + 15) + "px";
        tooltip.style.top = top + "px";
    }

    onMouseEnterFavoriteIcon(metric) {
        if (!this.favTooltip) return;

        if (this.isFavMetric(metric)) {
            this.favTooltip.textContent = this.cvLocalizationService
                .getCachedText("Metric_Selector", "Remove_From_Favorites", `Remove from favorites`);
        } else {
            this.favTooltip.textContent = this.cvLocalizationService
                .getCachedText("Metric_Selector", "Add_To_Favorites", `Add to favorites`);
        }
        this.positionTooltip(this.favTooltip);
        this.favTooltipShowing = true;
    }

    onMouseMoveFavoriteIcon() {
        if (!this.favTooltip) return;
        this.positionTooltip(this.favTooltip);
    }

    onMouseLeaveFavoriteIcon() {
        if (!this.favTooltip) return;
        this.favTooltipShowing = false;
    }

    positionAddFilter(el) {
        const top = this.calculateTop(el);

        el.style.top = top + 15 + "px";
    }

    calculateTop(el) {
        let top = 0;
        if (this.mouseY < window.innerHeight / 2)
            top = this.mouseY;
        else if (this.mouseY < window.innerHeight * 3 / 4)
            top = this.mouseY - el.offsetHeight / 2;
        else
            top = this.mouseY - el.offsetHeight;
        return top;
    }

    showFilterMenu(event, id, fav = false) {
        if (!this.data.HasFilters)
            return;

        event.preventDefault();
        if (this.previousDropdown) {
            this.previousDropdown.classList.remove('d-block');
            this.previousDropdown = null;
        }

        const metricFilterOption = document.getElementById(fav ? `msFilterOptionFav-${id}` : `msFilterOption-${id}`);
        metricFilterOption.addEventListener('contextmenu', e => e.preventDefault());
        metricFilterOption.classList.add("d-block");
        this.previousDropdown = metricFilterOption;
        this.positionAddFilter(metricFilterOption);
    }

    openFilterModal(content, metric, filterSection = false) {
        event.stopPropagation();
        if (!this.data.HasFilters)
            return;

        this.metricToFilter = metric;
        this.filterMetric = this.getFilterMetric(metric.MetricID);

        if (metric.NumericType === 3) {
            this.minValue = this.getTime(this.filterMetric.MinValue);
            this.maxValue = this.getTime(this.filterMetric.MaxValue);
        }
        else {
            this.minValue = this.filterMetric.MinValue;
            this.maxValue = this.filterMetric.MaxValue;
        }

        this.ngbModal.open(content, {
            backdrop: false,
            windowClass: filterSection ? 'metricFilterSectionModal' : 'metricFilterModal',
            centered: true,
        });
        document.getElementById('filterMin').focus();
    }

    getFilterMetric(id): SelectorFilterMetric {
        const filterMatch = this.data.MetricFilters?.find(m => m.MetricID === id);
        if (!filterMatch)
            return { MetricID: id, MaxValue: null, MinValue: null };
        return filterMatch;
    }

    hasFilter(id) {
        if (!this.data.HasFilters)
            return false;
        return this.data.MetricFilters?.find(m => m.MetricID === id);
    }

    cleanKeyPress(event: any, metric: SelectorMetric) {
        const type = metric.NumericType;
        const inputChar = String.fromCharCode(event.charCode);
        let cleanedChar = '';
        let currentValue = event.target.value;

        switch (type) {
            // currency
            case 0:
            // number
            // eslint-disable-next-line no-fallthrough
            case 1:
            // percent
            // eslint-disable-next-line no-fallthrough
            case 2: {
                cleanedChar = inputChar.replace(/[^0-9.]/g, '');
                if (cleanedChar !== '') {
                    currentValue += cleanedChar;
                    const segments = currentValue.split('.');
                    if (segments.length > 2) {
                        cleanedChar = '';
                    }
                }
                break;
            }
            // time
            case 3: {
                cleanedChar = inputChar.replace(/[^0-9:]/g, '');
                if (cleanedChar !== '') {
                    currentValue += cleanedChar;
                    const segments = currentValue.split(':');
                    if (segments.length > 3) {
                        cleanedChar = '';
                    }
                }
                break;
            }
        }
        if (cleanedChar === '') {
            // invalid character or format, prevent input
            event.preventDefault();
        }
    }

    isMinMaxValid() {
        const isTime = (this.minValue + '').includes(':') || (this.maxValue + '').includes(':');
        let min = 0;
        let max = 0;
        if (this.minValue && this.maxValue) {
            if (isTime) {
                min = this.getSeconds(this.minValue);
                max = this.getSeconds(this.maxValue);
            }
            else {
                min = parseFloat(this.minValue);
                max = parseFloat(this.maxValue);
            }
            return min < max;
        }
        return true;
    }

    convertString(value) {
        if (!value)
            return;

        const isTime = (value + '').includes(':');
        if (isTime) {
            return this.getSeconds(value);
        }
        else {
            return parseFloat(value);
        }
    }

    onSaveFilterMetric(metric: SelectorMetric) {
        if ((!this.minValue || this.minValue === "") && (!this.maxValue || this.maxValue === "")) {
            this.onRemoveFilter(metric.MetricID);
            return;
        }

        const filterMatch = this.data.MetricFilters?.find(m => m.MetricID === this.filterMetric.MetricID);
        this.filterMetric.MinValue = this.convertString(this.minValue);
        this.filterMetric.MaxValue = this.convertString(this.maxValue);
        this.filterMetric.Abbreviation = metric.Abbreviation;
        this.filterMetric.MetricCustomName = metric.MetricCustomName;
        this.filterMetric.NumericType = metric.NumericType;
        if (!filterMatch) {
            this.data.MetricFilters.push({
                MetricID: this.filterMetric.MetricID,
                MinValue: this.filterMetric.MinValue,
                MaxValue: this.filterMetric.MaxValue,
            } as SelectorFilterMetric);
            this.filterMetrics.push(this.filterMetric);
        }
        else {
            const index = this.data.MetricFilters.findIndex(m => m.MetricID === this.filterMetric.MetricID);
            if (!filterMatch.MinValue && !filterMatch.MaxValue) { //User emptied values
                if (index > -1) {
                    this.data.MetricFilters.splice(index, 1); //remove filter
                    this.filterMetrics.splice(index, 1);
                }
            }
            else {
                if (index > -1) {
                    this.data.MetricFilters[index] = this.filterMetric; //update filter
                    this.filterMetrics[index] = this.filterMetric;
                }
            }
        }
    }

    onRemoveFilter(id?: number) {
        const filterMatch = this.data.MetricFilters?.find(m => m.MetricID === id);
        if (filterMatch) {
            const index = this.data.MetricFilters.findIndex(m => m.MetricID === id);
            if (index > -1) {
                this.data.MetricFilters.splice(index, 1);
                this.filterMetrics.splice(index, 1);
            }
        }
    }

    getSeconds(value: string) {
        let seconds = 0;
        const segments = value.split(':');
        for (let i = 0; i <= segments.length; i++) {
            const parsed = parseInt(segments[i], 10);
            if (Number.isNaN(parsed))
                seconds += 0;
            else
                seconds += parsed * Math.pow(60, segments.length - (i + 1));
        }
        return seconds;
    }

    getTime(value: number): string {
        if (value && typeof (value) === 'number') {
            const hour = (Math.floor(value / 3600) > 99) ? Math.floor(value / 3600) : ("0" + Math.floor(value / 3600)).slice(-2);
            return (hour + ":" + ("0" + Math.floor(value / 60) % 60).slice(-2) + ":" + ("0" + value % 60).slice(-2));
        }
    }

    toggleStandard(popover, metric) {
        if (this.currentMetric?.MetricID !== metric.MetricID && this.currentMetric != null) {
            this.currentPopover.close();
        }
        this.currentMetric = metric;
        this.currentPopover = popover;
        popover.open();
        this.showDetails = false;
    }

    openPopover(popover, metric, selected, isInfoIcon = false) {
        if ((selected >= this.service.maxMetrics) && !this.isSelected(metric) && !isInfoIcon)
            return;

        if (this.popoverTimeout) {
            clearTimeout(this.popoverTimeout);
            this.popoverTimeout = null;
        }

        this.popoverTimeout = setTimeout(() => {
            this.toggleStandard(popover, metric);
        }, 1000);
    }

    cancelTimeout() {
        clearTimeout(this.popoverTimeout);
    }

    closePopover() {
        this.currentPopover?.close();
        this.cancelTimeout();
    }

    async toggleMore(metric: SelectorMetric) {
        this.showDetails = !this.showDetails;
        const compPre = document.getElementById(`componentPreview-${metric.MetricID}`);
        const calcPre = document.getElementById(`calculatedPreview-${metric.MetricID}`);
        const preview = compPre ?? calcPre;
        if (preview){
            if (preview.scrollHeight <= preview.offsetHeight) {
                preview.classList.add('componentScroll');
            }
        }
        //Mongo will use MetricTypes and Legacy uses MetricType
        if (metric?.MetricDefType === MetricTypes.Calculated || metric.MetricType === MetricType.Derived) {
            if (!this.service.allMetricsLoaded) { //only load all metrics if they havent been loaded before
                try {
                    this.formulaLoading = true;
                    await this.service.getAllMetrics();
                }
                catch {
                    //do nothing
                }
            }
            if (!metric.FormulaLoaded) {
                this.getFormula(metric);
            }
            this.formulaLoading = false;
        }
    }

    getFormula(metric: SelectorMetric) {
        let formula = metric.Formula;
        const reg = /\d+/g;
        let match;
        while ((match = reg.exec(metric.Formula)) !== null) {
            const metricMatch = this.service.allMetrics.find(m => m.MetricID === parseInt(match, 10));
            if(metricMatch)
                formula = formula.replace('#' + match + '#', metricMatch.Abbreviation);
        }
        metric.CalculatedFormula = formula;
        metric.FormulaLoaded = true;
    }

    getDirectionDescText() {
        let directionDesc;
        switch (this.currentMetric?.DefaultDirection) {
            case 0:
                directionDesc = this.cvLocalizationService.getCachedText("Metric_Selector", "Description_Down", "Low metric value is best");
                break;
            case 1:
                directionDesc = this.cvLocalizationService.getCachedText("Metric_Selector", "Description_Up", "High metric value is best");
                break;
            default:
                directionDesc = "";
                break;
        }
        return directionDesc;
    }

    getLocalizedRelatedMetricsTooltip() {
        return this.cvLocalizationService.getCachedText("Metric_Selector", "Related_Metrics_Tooltip", "These metrics are specifically chosen to match your previous selections.")
    }
    
    onSelectedMetricDrop(event: CdkDragDrop<string[]>) {
        moveItemInArray(this.selectedMetrics, event.previousIndex, event.currentIndex);
    }

    loadMetricDateFields(metric: SelectedMetric): void {
        let dateFields;
        if (metric.Components?.length > 0) {
            dateFields = metric.Components.reduce((acc, component) => {
                if (component.DateFields)
                    return acc.concat(component.DateFields);
                else
                    return acc;
            }, []);

            dateFields = Array.from(new Set(dateFields));
            metric.DateFields = dateFields;
        } else {
            metric.DateFields = [];
        }
    }
    
    setDefaultDateParadigm(metric: SelectedMetric): void {
        if (!metric.Components)
            return; 

        if (metric.DateFields.length > 0 && metric.Components[0]?.StartDate != null) {
            // Check if StartDate is already part of DateFields
            if (!metric.DateFields.includes(metric.Components[0].StartDate))
                metric.DateFields.unshift(metric.Components[0].StartDate);
                
            metric.DateParadigm = metric.Components[0].StartDate;
        } else if ((metric.DateFields.length == 0 || metric.Components[0]?.DateFields == null) 
            && metric.Components[0]?.StartDate != null) {
            metric.DateParadigm = metric.Components[0].StartDate;
        } 
        return;
    }

    getDateParadigmDisplayName(name: string): string {
        let localizedName: string = this.cvLocalizationService.getCachedText("Date_Paradigm_Filter", name, name);
        return localizedName;
    }
}
